<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed, watch } from 'vue';
import NavbarMobileMainNavigation from '@layout/nav-v2/mobile/components/NavbarMobileMainNavigation.vue';
import NavbarMobilePlus from '@layout/nav-v2/mobile/components/NavbarMobilePlus.vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { storeToRefs } from 'pinia';
import { SelectionItemEvent } from '@enums/event.ts';
import { useDataReady } from '@composables/useDataReady.ts';

const navbarStore = useNavbarStore();
const { isPlusMenuOpen, isMobileNavbarVisible } = storeToRefs(navbarStore);
const { closePlusMenu, shouldEnableScrollHide } = navbarStore;

const { isDataReady, setDataIsReady } = useDataReady();

const isVisibleByScroll = ref<boolean>(true);
const lastTopPositionY = ref<number>(0);

const emitVisibilityEvent = (visible: boolean): void => {
  if (typeof window === 'undefined') return;
  const type = visible ? SelectionItemEvent.MOBILE_NAVBAR_VISIBLE : SelectionItemEvent.MOBILE_NAVBAR_HIDDEN;
  window.dispatchEvent(new CustomEvent(type));
};

const onScroll = (): void => {
  const header = 80;
  const margin = 8;
  const y = window.scrollY || 0;
  const delta = y - lastTopPositionY.value;

  if (Math.abs(delta) < margin) {
    return;
  }

  if (delta > 0 && y > header) {
    isVisibleByScroll.value = false;
    if (isPlusMenuOpen.value) {
      closePlusMenu();
    }
  } else {
    isVisibleByScroll.value = true;
  }

  lastTopPositionY.value = y;
};

const isScrollListenerEnabled = ref<boolean>(false);

const navbarTranslateClass = computed<string | null>(() => {
  if (!isScrollListenerEnabled.value || isPlusMenuOpen.value) {
    return null;
  }
  return isVisibleByScroll.value ? 'translate-y-0' : 'translate-y-full';
});

watch(isVisibleByScroll, (visible) => {
  emitVisibilityEvent(visible);
}, { immediate: true });

onMounted(() => {
  setDataIsReady();

  if (typeof window === 'undefined') return;

  isScrollListenerEnabled.value = shouldEnableScrollHide();

  if (!isScrollListenerEnabled.value) {
    isVisibleByScroll.value = true;
    return;
  }

  lastTopPositionY.value = window.scrollY || 0;
  window.addEventListener('scroll', onScroll, { passive: true });
});

onBeforeUnmount(() => {
  if (!isScrollListenerEnabled.value) {
    return;
  }
  window.removeEventListener('scroll', onScroll);
});
</script>

<template>
  <nav
    v-if="isMobileNavbarVisible"
    :class="[
      'fixed bottom-0 z-[101]',
      'w-full h-16',
      'flex lg:hidden',
      { 'transition-transform duration-800 ease-out': isScrollListenerEnabled && !isPlusMenuOpen },
      navbarTranslateClass,
    ]"
    :data-ready="isDataReady"
    data-cy-ref="navbar-mobile"
  >
    <NavbarMobileMainNavigation class="flex-1 bg-white border-t border-gray" />

    <NavbarMobilePlus
      class="!z-[-1]"
      :is-open="isPlusMenuOpen"
      data-cy-ref="navbar-mobile__plus"
      @close="closePlusMenu"
    />
  </nav>
</template>
